import { ReactElement, useMemo, useRef, useState } from 'react'
import {
  DrawControllerWrapper,
  hashObject,
  useTapelineCentersSnapTargets,
  useTapelineSnapTargets,
} from '@scene'
import { find, isNull } from 'lodash-es'
import {
  InteractiveLine,
  InteractiveLineHandles,
  InteractiveLineRef,
  TransientDrawState,
  useCanvasListener,
  ImmutableVector3,
  getCenterMultiple,
} from '@modugen/ui-toolkit'
import { Torus } from '@react-three/drei'
import { useControlStore, useModelStore } from '@editorStores'
import { useClickActionEnabled } from '@editorHooks'
import { useStructuralPlanningDrawerEsc } from '@structuralPlanningHooks'
import {
  SnapTargetElementType,
  useDefault2DSnapConfigForElementType,
} from 'src/components/pages/Editor/hooks/useDefaultSnapConfigForElementType'
import { useModelSnapTargets } from '../../hooks'
import { commitPurlinHandlePoint, flattenToXY, hasUsablePurlinXYAxis } from './utils'

const interactiveLineName = 'interactive-purlin-line'

interface Props {
  purlinGuid: string
  onEditStart: () => void
  onEditStop: () => void
  reset: () => void
}

const PurlinEdit = ({ purlinGuid, onEditStart, onEditStop }: Props): ReactElement | null => {
  const lineRef = useRef<InteractiveLineRef>(null)

  const { purlins } = useModelStore(state => state.model)
  const updatePurlin = useModelStore(state => state.updatePurlin)

  const purlin = useMemo(() => find(purlins, ['guid', purlinGuid]), [purlins, purlinGuid]) as
    | ShapeObjectLine
    | undefined

  const [activeHandle, setActiveHandle] = useState<
    typeof InteractiveLineHandles[keyof typeof InteractiveLineHandles] | null
  >(null)

  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const snapToAngles = useControlStore(state => state.snapToAngles)
  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)

  const modelSnapTargets = useModelSnapTargets({
    xyOnly: true,
  })
  const tapelineSnapTargets = useTapelineSnapTargets(true)
  const tapelineCenterTargets = useTapelineCentersSnapTargets(true)

  const additionalSnapTargets = useMemo(
    () => [...modelSnapTargets, ...tapelineSnapTargets, ...tapelineCenterTargets],
    [modelSnapTargets, tapelineSnapTargets, tapelineCenterTargets],
  )

  useDefault2DSnapConfigForElementType(SnapTargetElementType.PURLINS)

  useStructuralPlanningDrawerEsc(() => {
    onEditStop()
    if (activeHandle) {
      setActiveHandle(null)
      lineRef.current?.reset()
    }
  }, !!activeHandle)

  const purlinStartXY = useMemo(() => (purlin ? flattenToXY(purlin.shape.start) : null), [purlin])
  const purlinEndXY = useMemo(() => (purlin ? flattenToXY(purlin.shape.end) : null), [purlin])

  const canEditAlongXYAxis = useMemo(
    () => (purlin ? hasUsablePurlinXYAxis(purlin.shape.start, purlin.shape.end) : false),
    [purlin],
  )

  const purlinCenterXY = useMemo(() => {
    if (!purlinStartXY || !purlinEndXY) return null
    return getCenterMultiple([purlinStartXY, purlinEndXY])
  }, [purlinStartXY, purlinEndXY])

  const purlinDirectionXY = useMemo(() => {
    if (!purlinStartXY || !purlinEndXY || !canEditAlongXYAxis) return null
    return purlinEndXY.sub(purlinStartXY).normalize()
  }, [purlinStartXY, purlinEndXY, canEditAlongXYAxis])

  const editStop = () => {
    if (!purlin) return

    const purlinLine = lineRef.current?.transientLine.current
    if (!purlinLine || !activeHandle) return

    const [drawerStart, drawerEnd] = purlinLine
    const handlePoint = activeHandle === InteractiveLineHandles.Start ? drawerStart : drawerEnd
    const committed = commitPurlinHandlePoint(purlin.shape.start, purlin.shape.end, handlePoint)
    if (!committed) return

    const start =
      activeHandle === InteractiveLineHandles.Start
        ? committed
        : new ImmutableVector3(purlin.shape.start.x, purlin.shape.start.y, purlin.shape.start.z)
    const end =
      activeHandle === InteractiveLineHandles.End
        ? committed
        : new ImmutableVector3(purlin.shape.end.x, purlin.shape.end.y, purlin.shape.end.z)

    updatePurlin({
      guid: purlin.guid,
      shape: { start, end },
    })
  }

  const stopEditing = () => {
    editStop()
    setActiveHandle(null)
    onEditStop()
  }

  useCanvasListener(
    'mouseup',
    event => {
      const isInput = event.target === lineRef.current?.input.current

      if (!isInput && !lineRef.current?.inputActive.current && activeHandle) {
        stopEditing()
      }
    },
    [activeHandle, editStop, stopEditing],
  )

  const extendLine = (transientDrawState: TransientDrawState) => {
    if (lineRef.current?.inputActive.current) return

    if (activeHandle && transientDrawState.drawPoint) {
      lineRef.current?.updateHandle(activeHandle, flattenToXY(transientDrawState.drawPoint))
    }
  }

  const onExtendStart = (
    handle: typeof InteractiveLineHandles[keyof typeof InteractiveLineHandles],
  ) => {
    if (!canEditAlongXYAxis) return

    if (isNull(activeHandle)) {
      onEditStart()
      setActiveHandle(handle)
    }
  }

  const zTranslation = 10
  const drawControllerEnabled = useClickActionEnabled()
  const showHandles = !!purlin && !purlin.is_local && canEditAlongXYAxis

  if (!purlin || !purlinStartXY || !purlinEndXY) {
    return null
  }

  return (
    <>
      {purlinCenterXY && purlinDirectionXY && (
        <DrawControllerWrapper
          drawingAxis={{
            origin: purlinCenterXY,
            direction: purlinDirectionXY,
          }}
          indicatorType="crosshair"
          enableIndicator={false}
          enabled={!!activeHandle && drawControllerEnabled && canEditAlongXYAxis}
          onMouseMove={extendLine}
          additionalSnapTargets={additionalSnapTargets}
          snapToAngles={snapToAngles}
          snapToCornersAndEdges={snapToCornersAndEdges}
          orthoSnap={snapOrthogonal}
          xyOnly
        />
      )}

      <group position={[0, 0, zTranslation]}>
        <InteractiveLine
          key={hashObject(purlin)}
          mainLineProps={{ name: interactiveLineName }}
          boundingLineProps={{ name: interactiveLineName }}
          ref={lineRef}
          line={[purlinStartXY, purlinEndXY]}
          clickDisabled={!!activeHandle}
          showHandles={showHandles}
          onClickHandle={onExtendStart}
          clickHandleDisabled={!canEditAlongXYAxis}
          clickableHandles={activeHandle ? [activeHandle] : undefined}
          nonSelectable={!!activeHandle}
          cursor={showHandles ? 'grab' : 'auto'}
          indicatorCursor={showHandles ? 'grab' : 'auto'}
          handleColor={activeHandle ? 'purple' : undefined}
        />

        <Torus position={[purlinStartXY.x, purlinStartXY.y, 0]} args={[0.15, 0.01, 8, 16]}>
          <meshBasicMaterial color="red" />
        </Torus>
        <Torus position={[purlinEndXY.x, purlinEndXY.y, 0]} args={[0.15, 0.01, 8, 16]}>
          <meshBasicMaterial color="black" />
        </Torus>
      </group>
    </>
  )
}

export default PurlinEdit
